import { Injectable } from '@angular/core';
import {
  isFilterCalendarData,
  isFilterCustomerData,
  ModalFilterCalendarData,
  ModalFilterCustomerData,
} from '@shared/components/usf-filtering-modal/model/modal-filter';
import { CustomerReportingModalService } from './customer-reporting-modal.service';
import { BehaviorSubject, filter, take } from 'rxjs';
import { Store } from '@ngrx/store';
import {
  getCustomers,
  selectAppStateSelectedCustomer,
} from '@app/ngrx-customer/store';
import dayjs from 'dayjs';
import { CustomerSelectionOption } from '@app/customer/models/customer-selection-option.model';
import { SelectedCustomerState } from '@panamax/app-state';
import { Router, NavigationEnd } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class CustomerReportingFilterService {
  private fromDateSub = new BehaviorSubject<number>(0);
  private toDateSub = new BehaviorSubject<number>(0);
  private deliveryDateRangeSub = new BehaviorSubject<string>('');
  private numberSelectedFiltersSub = new BehaviorSubject<number>(2);
  private selectedCustomersSub = new BehaviorSubject<CustomerSelectionOption[]>(
    [],
  );
  private customerDivisionFilterSub = new BehaviorSubject<any[]>([]);
  private filtersUpdatedSub = new BehaviorSubject<boolean>(false);

  private defaultCustomer: SelectedCustomerState;

  public fromDate$ = this.fromDateSub.asObservable();
  public toDate$ = this.toDateSub.asObservable();
  public deliveryDateRange$ = this.deliveryDateRangeSub.asObservable();
  public numberSelectedFilters$ = this.numberSelectedFiltersSub.asObservable();
  public selectedCustomers$ = this.selectedCustomersSub.asObservable();
  public customerDivisionFilter$ =
    this.customerDivisionFilterSub.asObservable();
  public filtersUpdated$ = this.filtersUpdatedSub.asObservable();

  private initialized = false;

  constructor(
    private store: Store,
    private customerReportingModal: CustomerReportingModalService,
    private router: Router,
  ) {
    this.setDefaultDates();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects || event.url;
        if (!url.includes('/customer-reporting') && this.initialized) {
          this.resetFilters();
          this.initialized = false;
        }
      });
  }

  private setDefaultDates() {
    const yesterday = dayjs().subtract(1, 'day');
    let fromDate = dayjs().subtract(6, 'months').subtract(1, 'day');
    if (fromDate.date() !== yesterday.date()) {
      fromDate = fromDate.endOf('month');
    }
    const toDate = yesterday;

    this.fromDateSub.next(this.getDateNumber(fromDate.toDate()));
    this.toDateSub.next(this.getDateNumber(toDate.toDate()));
    this.deliveryDateRangeSub.next(
      this.formattedDateLabel(this.fromDateSub.value, this.toDateSub.value),
    );
  }

  public initializeFilters() {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.setDefaultDates();

    this.store
      .select(getCustomers)
      .pipe(
        filter(customers => !!customers && customers.length > 0),
        take(1),
      )
      .subscribe(customers => {
        const defaultCustomer = customers[0];
        const selectedCustomers = [
          this.customerReportingModal.createCustomerSelectionOption(
            defaultCustomer,
            true,
          ),
        ];
        this.selectedCustomersSub.next(selectedCustomers);
        this.customerDivisionFilterSub.next(
          this.groupCustomersByDivision(selectedCustomers),
        );
      });
  }

  public resetFilters() {
    this.setDefaultDates();

    this.store
      .select(getCustomers)
      .pipe(
        filter(customers => !!customers && customers.length > 0),
        take(1),
      )
      .subscribe(customers => {
        const defaultCustomer = customers[0];
        const selectedCustomers = [
          this.customerReportingModal.createCustomerSelectionOption(
            defaultCustomer,
            true,
          ),
        ];
        this.selectedCustomersSub.next(selectedCustomers);
        this.customerDivisionFilterSub.next(
          this.groupCustomersByDivision(selectedCustomers),
        );
        this.numberSelectedFiltersSub.next(2);
      });
  }

  public onFilterChange(event) {
    if (event.role !== 'backdrop' && event.data) {
      const selectedFilters = [];
      event.data.filters.forEach(filter => {
        if (isFilterCalendarData(filter.data)) {
          const calendarData = filter.data as ModalFilterCalendarData;
          if (calendarData.fromDate && calendarData.toDate) {
            this.fromDateSub.next(calendarData.fromDate);
            this.toDateSub.next(calendarData.toDate);
            this.deliveryDateRangeSub.next(
              this.formattedDateLabel(
                calendarData.fromDate,
                calendarData.toDate,
              ),
            );
            selectedFilters.push({
              filterName: 'dateFilter',
              fromDate: calendarData.fromDate,
              toDate: calendarData.toDate,
            });
          }
        } else if (isFilterCustomerData(filter.data)) {
          const customerData = filter.data as ModalFilterCustomerData;
          if (customerData.selectedCustomers.length > 0) {
            this.selectedCustomersSub.next(customerData.selectedCustomers);
            customerData.selectedCustomers.forEach(customer =>
              selectedFilters.push({
                customerName: customer.customerName,
                customerNumber: customer?.customerNumber,
                customerDivision: customer?.divisionNumber,
              }),
            );
            this.customerDivisionFilterSub.next(
              this.groupCustomersByDivision(customerData.selectedCustomers),
            );
          }
        }
      });

      this.numberSelectedFiltersSub.next(selectedFilters.length);
      this.filtersUpdatedSub.next(true);
      return true;
    }
    this.filtersUpdatedSub.next(false);
    return false;
  }

  private getDateNumber(date: Date): number {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return Number(`${year}${month}${day}`);
  }

  private formattedDateFromNumber(date: number) {
    if (!!date) {
      const dateString = date.toString();
      return (
        dateString.substring(4, 6) +
        '/' +
        dateString.substring(6, 8) +
        '/' +
        dateString.substring(0, 4)
      );
    }
    return 'MM/DD/YYYY';
  }

  private formattedDateLabel(fromDate: number, toDate: number) {
    return (
      this.formattedDateFromNumber(fromDate) +
      ' - ' +
      this.formattedDateFromNumber(toDate)
    );
  }

  private groupCustomersByDivision(customers: CustomerSelectionOption[]) {
    const grouped: { [key: string]: any } = {};
    customers.forEach(customer => {
      if (!customer) return;
      const division = customer.divisionNumber;
      const customerNumber = customer.customerNumber;
      if (!grouped[division]) {
        grouped[division] = { divisionNumber: division, customers: [] };
      }
      grouped[division].customers.push(customerNumber);
    });
    return Object.values(grouped);
  }

  get fromDate() {
    return this.fromDateSub.value;
  }
  get toDate() {
    return this.toDateSub.value;
  }

  get selectedCustomers() {
    return this.selectedCustomersSub.value;
  }
}
